import {useEffect, useMemo, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useCustomQuery, useCustomerId, useUsid} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useBrand, toBrandCode} from '../contexts/brand-context'
import {useEnrichVickyProductsWithStatus} from './use-enrich-vicky-products'
import {useFypRecommendationPool} from './use-fyp-recommendation-pool'
import {FYP_RECOMMENDATION_MAX_COUNT} from '../constants'

/*
 * SCAPI getProducts accepts at most 24 ids per call and useEnrichVickyProducts does not
 * split its requests, so a page is enriched in two halves. The backend serves at most
 * 2 * ENRICH_BATCH_SIZE products per page.
 */
const ENRICH_BATCH_SIZE = 24

// Default For You load when the caller supplies no count.
const FYP_RECOMMENDATION_COUNT = 100

/**
 * @typedef {Object} FypRecosPLPPage
 * @property {Object[]} hits - Products mapped to the PLP search-hit shape.
 * @property {number} total - Backend count of resolved, online, orderable products.
 * @property {number} offset - Offset of this response, which may belong to a retained page.
 */

/**
 * Fetches one page of the Vicky "For You" (fyp-recos) ranking for a category PLP and
 * returns it in the shape of a PLP product search result, so the PLP can paginate it
 * like a regular category search.
 *
 * The fyp-recos backend resolves Vicky's SKUs to orderable products in Vicky's order
 * and reports the total number of orderable ranked products. The recommendations
 * (`c_response=ids`) backend returns Vicky's ids as-is, and this hook resolves and
 * filters them. Personalization parameters are built on the server. In
 * `recommendations` mode the request carries `c_uid` (the shopper id) so SCAPI keys
 * its cache per shopper and one shopper's personalized list can never be served to
 * another.
 *
 * @param {Object} options
 * @param {string} options.categoryId - SFCC category id (Vicky's `category_id`).
 * @param {number} options.offset - Offset of the first product of the page.
 * @param {number} options.limit - Page size (at most 48).
 * @param {boolean} [options.enabled=true] - Request gate; the PLP caller disables it during SSR.
 * @returns {{page: FypRecosPLPPage|null, isFailed: boolean}} A page after enrichment
 *   requests settle, or the previous page of the same category while loading. Consumers
 *   must match its offset before advancing pagination. `isFailed` covers custom API
 *   errors/unusable responses, not enrichment errors. An empty valid page is handled
 *   by the caller's legacy fallback.
 */
export const useFypRecosPLP = ({
    categoryId,
    offset,
    limit,
    enabled = true,
    useRecommendations = false,
    recommendationCount = FYP_RECOMMENDATION_COUNT
}) => {
    const location = useLocation()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    const {brandId} = useBrand()
    const customerId = useCustomerId()
    const {usid} = useUsid()
    const shopperId = customerId || usid || undefined
    const localeId = typeof locale === 'string' ? locale : locale?.id
    const country = localeId?.split(/[-_]/).pop()?.toUpperCase() || 'CA'
    // Vicky expects 'UK' where the locale says 'GB'.
    const countryCode = country === 'GB' ? 'UK' : country
    const brandCode = toBrandCode(brandId)
    const isEnabled =
        enabled && Boolean(siteId) && (useRecommendations ? Boolean(shopperId) : Boolean(categoryId))

    // The "For You" surfaces use the vickyai GET /recommendations endpoint in its
    // opt-in FYP mode (c_response=ids): it returns only Vicky's ordered product ids,
    // with the shopper identity resolved server-side from the token. The PLP ranking
    // keeps the category-scoped fyp-recos endpoint.
    const endpointPath = useRecommendations ? '/recommendations' : '/fyp-recos'
    const numItems = Math.min(Math.max(recommendationCount, 1), FYP_RECOMMENDATION_MAX_COUNT)
    const parameters = useRecommendations
        ? {
              siteId,
              c_brand: brandCode,
              c_country: countryCode,
              c_uid: shopperId,
              c_response: 'ids',
              c_num_items: numItems,
              ...(localeId ? {locale: localeId} : {})
          }
        : {
              siteId,
              c_brand: brandCode,
              c_country: countryCode,
              c_categoryId: categoryId,
              c_offset: offset,
              c_limit: limit,
              ...(localeId ? {locale: localeId} : {})
          }

    const {
        data,
        isLoading: isQueryLoading,
        isError
    } = useCustomQuery(
        {
            options: {
                method: 'GET',
                parameters,
                customApiPathParameters: {
                    apiVersion: 'v1',
                    endpointPath,
                    apiName: 'vickyai'
                }
            },
            rawResponse: false
        },
        {
            queryKey: useRecommendations
                ? ['vickyai', endpointPath, siteId, shopperId, numItems]
                : ['vickyai', endpointPath, categoryId, siteId, customerId, offset, limit],
            enabled: isEnabled,
            staleTime: 5 * 60 * 1000,
            // Fail fast: a failure falls back to the standard PLP, so don't keep shoppers waiting.
            retry: false,
            onError: (err) => {
                console.warn(`VickyAI ${endpointPath} fetch failed:`, err)
            }
        }
    )

    // fyp-recos answers `{data, total}` (one server page). The recommendations FYP
    // mode answers `{skus, total}` with the whole ordered list. Anything else (an
    // empty array when the Vicky call failed, or a "not enabled" message) is not a
    // usable ranking.
    const isUsable = useRecommendations
        ? Array.isArray(data?.skus)
        : Array.isArray(data?.data) && Number.isFinite(data?.total)

    // recommendations mode answers ids only. Resolve them against SFCC the same way
    // smart search does (utils/smart-search.js) so ids that do not resolve are dropped
    // everywhere and `total` is the number of products that can actually render — not
    // the number of ids Vicky returned.
    const recommendedIds = useMemo(
        () => (useRecommendations && isUsable ? data.skus : []),
        [useRecommendations, isUsable, data]
    )
    const pool = useFypRecommendationPool(recommendedIds, {
        enabled: isEnabled && useRecommendations && isUsable
    })
    const isPoolLoading = useRecommendations && pool.isLoading

    const ranked = useMemo(() => {
        if (!isUsable) return []
        return useRecommendations ? pool.ids.map((id) => ({id})) : data.data
    }, [isUsable, useRecommendations, pool.ids, data])
    const items = useMemo(
        () => (useRecommendations ? ranked.slice(offset, offset + limit) : ranked),
        [useRecommendations, ranked, offset, limit]
    )
    const total = useRecommendations ? pool.total : data?.total
    const firstHalf = useMemo(() => items.slice(0, ENRICH_BATCH_SIZE), [items])
    const secondHalf = useMemo(() => items.slice(ENRICH_BATCH_SIZE), [items])

    // fyp-recos resolves orderable products on the backend, so it needs no availability
    // pass. The general recommendations endpoint does not, so its tiles get live stock
    // enriched the same way PLP tiles are.
    const enrichOptions = {
        enabled: isEnabled && isUsable,
        availabilityEnabled: useRecommendations
    }
    const {products: enrichedFirst, isFetching: isFirstFetching} = useEnrichVickyProductsWithStatus(
        firstHalf,
        enrichOptions
    )
    const {products: enrichedSecond, isFetching: isSecondFetching} =
        useEnrichVickyProductsWithStatus(secondHalf, enrichOptions)

    // Wait for both enrichment batches to settle before publishing. The enrichment hook
    // does not expose errors here, so completion does not guarantee every detail resolved.
    const readyPage = useMemo(() => {
        if (!isUsable || isPoolLoading || isFirstFetching || isSecondFetching) return null
        return {
            // Map the enriched items to the search-hit fields the grid reads.
            hits: [...enrichedFirst, ...enrichedSecond]
                .filter((product) => product?.id && (!useRecommendations || Boolean(product?.name)))
                .map((product) => ({
                    ...product,
                    productId: product.id,
                    productName: product.name,
                    hitType: 'product'
                })),
            total,
            offset: Number(data.offset ?? offset)
        }
    }, [isUsable, useRecommendations, isPoolLoading, isFirstFetching, isSecondFetching, enrichedFirst, enrichedSecond, data, total, offset])

    // Keep the previous page of the same category while the next one loads, so the grid
    // does not fall back to skeletons on "show more".
    const lastPageRef = useRef(null)
    if (readyPage) lastPageRef.current = {categoryId, page: readyPage}
    const page =
        readyPage ||
        (lastPageRef.current?.categoryId === categoryId ? lastPageRef.current?.page : null) ||
        null

    // A display indicator only. Native history avoids router navigation, PLP resets
    // and refetches. Reapply after pagination/navigation, preserving history state.
    const hasRenderedProducts = isEnabled && Boolean(page?.hits.length)
    useEffect(() => {
        const url = new URL(window.location.href)
        if (hasRenderedProducts) {
            if (url.searchParams.get('isPLPPersonalized') === 'true') return
            url.searchParams.set('isPLPPersonalized', 'true')
        } else {
            if (!url.searchParams.has('isPLPPersonalized')) return
            url.searchParams.delete('isPLPPersonalized')
        }
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`)
    }, [hasRenderedProducts, location])

    return {
        page,
        isFailed: isError || (isEnabled && !isQueryLoading && !isUsable)
    }
}
